import { stripHtmlToText } from '@/shared/utils/strip-html-to-text';

import type { Menu, MenuRecipe, MenuSection } from '../types/menu.types';

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function readPlainText(value: unknown, fallback = ''): string {
  return stripHtmlToText(readString(value, fallback));
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function readBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true' || value === 1 || value === '1') {
    return true;
  }
  if (value === 'false' || value === 0 || value === '0') {
    return false;
  }
  return fallback;
}

function readAllergens(value: unknown): MenuRecipe['allergens'] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is MenuRecipe['allergens'][number] => typeof item === 'string',
  );
}

export function normalizeRecipe(raw: unknown): MenuRecipe | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    menuRecipeId: readNumber(item.menuRecipeId),
    customizable: readBoolean(item.customizable),
    componentRecipeIds: Array.isArray(item.componentRecipeIds)
      ? item.componentRecipeIds.map((v) => readNumber(v))
      : [],
    ingredientIds: Array.isArray(item.ingredientIds)
      ? item.ingredientIds.map((v) => readNumber(v))
      : [],
    ingredientCategoryOrder: Array.isArray(item.ingredientCategoryOrder)
      ? item.ingredientCategoryOrder.map((v) => readNumber(v))
      : [],
    minIngredientChoices: readNumber(item.minIngredientChoices),
    maxIngredientChoices: readNumber(item.maxIngredientChoices, 30),
    showIngredientsMenu: readBoolean(item.showIngredientsMenu, true),
    showIngredientsTakeaway: readBoolean(item.showIngredientsTakeaway, true),
    showIngredientsDelivery: readBoolean(item.showIngredientsDelivery, true),
    takeawayEnabled: readBoolean(item.takeawayEnabled, true),
    deliveryEnabled: readBoolean(item.deliveryEnabled, true),
    takeawayPrice:
      item.takeawayPrice == null ? null : readNumber(item.takeawayPrice),
    deliveryPrice:
      item.deliveryPrice == null ? null : readNumber(item.deliveryPrice),
    vegetarian: readBoolean(item.vegetarian),
    vegan: readBoolean(item.vegan),
    ingredients: Array.isArray(item.ingredients)
      ? item.ingredients.filter((v): v is string => typeof v === 'string')
      : [],
    variants: normalizeIngredientGroups(item.variants),
    name: readString(item.name),
    description: readPlainText(item.description),
    nameEn: readString(item.nameEn),
    nameEs: readString(item.nameEs),
    nameFr: readString(item.nameFr),
    descriptionEn: readPlainText(item.descriptionEn),
    descriptionEs: readPlainText(item.descriptionEs),
    descriptionFr: readPlainText(item.descriptionFr),
    recipeCode: readString(item.recipeCode),
    price: readNumber(item.price),
    bestSeller: readBoolean(item.bestSeller),
    positionIndex: readNumber(item.positionIndex),
    weight: readString(item.weight),
    photoUrl: readString(item.photoUrl),
    allergens: readAllergens(item.allergens),
    year: readNumber(item.year),
    wineType: readString(item.wineType),
    grapeVariety: readString(item.grapeVariety),
    alcoholPercentage: readNumber(item.alcoholPercentage),
    cupPrice: readNumber(item.cupPrice),
    cellar: readString(item.cellar),
    region: readString(item.region),
    // Backend mixes naming: `isPricePerWeight` / `isVisible` / `isWine`.
    pricePerWeight: readBoolean(item.isPricePerWeight ?? item.pricePerWeight),
    visible: readBoolean(item.isVisible ?? item.visible, true),
    wine: readBoolean(item.wine ?? item.isWine ?? item.Wine ?? item.is_wine),
  };
}

export function normalizeSection(raw: unknown): MenuSection | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const recipes = Array.isArray(item.recipes)
    ? item.recipes
        .map((recipe) => normalizeRecipe(recipe))
        .filter((recipe): recipe is MenuRecipe => recipe !== null)
    : [];

  return {
    sectionId: readNumber(item.sectionId),
    sectionCode: readString(item.sectionCode),
    name: readString(item.name),
    description: readPlainText(item.description),
    nameEn: readString(item.nameEn),
    nameEs: readString(item.nameEs),
    nameFr: readString(item.nameFr),
    descriptionEn: readPlainText(item.descriptionEn),
    descriptionEs: readPlainText(item.descriptionEs),
    descriptionFr: readPlainText(item.descriptionFr),
    positionIndex: readNumber(item.positionIndex),
    globalMenuPrice: readNumber(item.globalMenuPrice),
    showRecipePrices: readBoolean(
      item.isShowRecipePrices ?? item.showRecipePrices,
      true,
    ),
    recipes,
    visible: readBoolean(item.isVisible ?? item.visible, true),
  };
}

export function normalizeMenu(raw: unknown): Menu | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const menuCode = readString(item.menuCode);
  if (!menuCode) {
    return null;
  }

  const sections = Array.isArray(item.sections)
    ? item.sections
        .map((section) => normalizeSection(section))
        .filter((section): section is MenuSection => section !== null)
    : [];

  return {
    menuId: readNumber(item.menuId),
    menuCode,
    branchCode: readString(item.branchCode),
    name: readString(item.name),
    description: readString(item.description),
    nameEn: readString(item.nameEn),
    nameEs: readString(item.nameEs),
    nameFr: readString(item.nameFr),
    descriptionEn: readString(item.descriptionEn),
    descriptionEs: readString(item.descriptionEs),
    descriptionFr: readString(item.descriptionFr),
    appliedTemplate: readString(item.appliedTemplate, 'template-default'),
    color: readString(item.color),
    logoMenuUrl: readString(item.logoMenuUrl),
    backgroundMenuUrl: readString(item.backgroundMenuUrl),
    sections,
    active: readBoolean(item.isActive ?? item.active, true),
    // Backend may misspell as `isRcipesImgVisible`; prefer correct spelling.
    isRecipesImgVisible: readBoolean(
      item.isRecipesImgVisible ?? item.isRcipesImgVisible,
      true,
    ),
  };
}

export function normalizeMenuList(raw: unknown): Menu[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizeMenu(item))
    .filter((item): item is Menu => item !== null);
}

function normalizeIngredientGroups(
  raw: unknown,
): NonNullable<MenuRecipe['variants']> {
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[])
    .filter((v): v is Record<string, unknown> => !!v && typeof v === 'object')
    .map((v) => ({
      name: readString(v.name, 'Ingredienti'),
      nameEn: readString(v.nameEn),
      nameEs: readString(v.nameEs),
      nameFr: readString(v.nameFr),
      minSelections: readNumber(v.minSelections),
      maxSelections: readNumber(v.maxSelections, 30),
      options: !Array.isArray(v.options)
        ? []
        : (v.options as unknown[])
            .filter(
              (o): o is Record<string, unknown> => !!o && typeof o === 'object',
            )
            .map((o) => ({
              id: o.id == null ? undefined : readNumber(o.id),
              code: readString(o.code),
              name: readString(o.name),
              description: readString(o.description),
              nameEn: readString(o.nameEn),
              nameEs: readString(o.nameEs),
              nameFr: readString(o.nameFr),
              price: readNumber(o.price),
              active: readBoolean(o.active, true),
              categoryId:
                o.categoryId == null ? undefined : readNumber(o.categoryId),
              category: readString(o.category),
              categoryEn: readString(o.categoryEn),
              categoryEs: readString(o.categoryEs),
              categoryFr: readString(o.categoryFr),
              allergens: readAllergens(o.allergens),
              vegetarian: readBoolean(o.vegetarian),
              vegan: readBoolean(o.vegan),
            })),
    }));
}
